import { useEffect, useState } from 'react'
import { useAuthStore, API_BASE } from '../store/auth'
import { apiClient } from '../api/client'

interface ReportSummary {
  year_income: number
  year_expenses: number
  year_net: number
  total_transactions: number
}

interface MonthlyData {
  month: string
  income: number
  expenses: number
}

function formatEuro(amount: number) {
  return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount)
}

export default function ReportsPage() {
  const { activeOrg } = useAuthStore()
  const [year, setYear] = useState(new Date().getFullYear().toString())
  const [summary, setSummary] = useState<ReportSummary | null>(null)
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!activeOrg) return
    setLoading(true)

    // Fetch dashboard data for summary
    apiClient.get<{
      financials?: { year_income: number; year_expenses: number; year_net: number };
      summary?: { total_transactions: number };
      monthly_trend?: MonthlyData[];
    }>(`/dashboard/overview?organization_id=${activeOrg.id}`)
      .then((data) => {
        setSummary({
          year_income: data.financials?.year_income || 0,
          year_expenses: data.financials?.year_expenses || 0,
          year_net: data.financials?.year_net || 0,
          total_transactions: data.summary?.total_transactions || 0
        })
        setMonthlyData(data.monthly_trend || [])
        setLoading(false)
      })
      .catch(() => {
        setError('Raporttien lataus epäonnistui')
        setLoading(false)
      })
  }, [activeOrg, year])

  const handleExport = (format: 'json' | 'csv') => {
    if (!activeOrg) return
    // Trigger download
    window.open(`${API_BASE}/reports/income-statement?organization_id=${activeOrg.id}&year=${year}&format=${format}`, '_blank')
  }

  if (!activeOrg) {
    return (
      <div className="p-8 text-gray-500">
        Valitse organisaatio vasemmasta valikosta.
      </div>
    )
  }

  if (loading) return <div className="p-8 text-gray-400">Ladataan...</div>
  if (error) return <div className="p-8 text-red-500">{error}</div>
  if (!summary) return null

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">📈 Raportit — {activeOrg.name}</h2>
        <div className="flex items-center gap-4">
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('json')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition"
            >
              JSON
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition"
            >
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-5">
          <div className="text-sm text-gray-500 mb-1">Tulot</div>
          <div className="text-2xl font-bold text-green-600">{formatEuro(summary.year_income)}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <div className="text-sm text-gray-500 mb-1">Menot</div>
          <div className="text-2xl font-bold text-red-500">{formatEuro(summary.year_expenses)}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <div className="text-sm text-gray-500 mb-1">Tulos</div>
          <div className={`text-2xl font-bold ${summary.year_net >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {formatEuro(summary.year_net)}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-5">
          <div className="text-sm text-gray-500 mb-1">Tapahtumat</div>
          <div className="text-2xl font-bold text-gray-800">{summary.total_transactions}</div>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Kuukausittainen kehitys</h3>
        {monthlyData.length === 0 ? (
          <p className="text-gray-400">Ei dataa</p>
        ) : (
          <div className="space-y-4">
            {monthlyData.map((month) => {
              const maxValue = Math.max(month.income, month.expenses) || 1
              const incomeWidth = (month.income / maxValue) * 100
              const expenseWidth = (month.expenses / maxValue) * 100
              
              return (
                <div key={month.month} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{month.month}</span>
                    <span className="text-gray-500">
                      Tulot: {formatEuro(month.income)} | Menot: {formatEuro(month.expenses)}
                    </span>
                  </div>
                  <div className="flex gap-2 h-6">
                    <div
                      className="bg-green-500 rounded"
                      style={{ width: `${incomeWidth}%` }}
                      title={`Tulot: ${formatEuro(month.income)}`}
                    />
                    <div
                      className="bg-red-500 rounded"
                      style={{ width: `${expenseWidth}%` }}
                      title={`Menot: ${formatEuro(month.expenses)}`}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href={`${API_BASE}/reports/income-statement?organization_id=${activeOrg.id}&year=${year}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white rounded-xl shadow p-5 hover:shadow-md transition cursor-pointer"
        >
          <h4 className="font-semibold text-gray-800 mb-2">Tuloslaskelma</h4>
          <p className="text-sm text-gray-500">Katso tuloslaskelma PDF-muodossa</p>
        </a>
        <a
          href={`${API_BASE}/reports/balance-sheet?organization_id=${activeOrg.id}&date=${year}-12-31`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white rounded-xl shadow p-5 hover:shadow-md transition cursor-pointer"
        >
          <h4 className="font-semibold text-gray-800 mb-2">Tase</h4>
          <p className="text-sm text-gray-500">Katso tase vuoden lopusta</p>
        </a>
        <a
          href={`${API_BASE}/reports/year-over-year?organization_id=${activeOrg.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white rounded-xl shadow p-5 hover:shadow-md transition cursor-pointer"
        >
          <h4 className="font-semibold text-gray-800 mb-2">Vuosien vertailu</h4>
          <p className="text-sm text-gray-500">Vertaa eri vuosien tuloksia</p>
        </a>
      </div>
    </div>
  )
}
